// src/views/BalanceView.jsx
import React, { useState, useEffect } from 'react';
import btcIcon from '../assets/btc-logo.png';
import ethIcon from '../assets/eth-logo.png';
import xcnIcon from '../assets/icono.png';
import clpIcon from '../assets/clp.png';
import { httpsCallable } from "firebase/functions";
import { functions, db } from '../firebaseConfig';
import { useWallet } from '../contexts/WalletContext';
import { useUser } from '../contexts/UserContext';
import { useWeb3 } from '../contexts/Web3Context';
import { useStaking } from '../hooks/useStaking';
import { ethers } from 'ethers';
import { HybridBrowserProvider } from '../utils/HybridBrowserProvider';
import { collection, query, where, limit, getDocs } from "firebase/firestore";

// --- URLs ---
const PRECIOS_URL = "https://southamerica-west1-xcoin-plataforma.cloudfunctions.net/obtenerPreciosConConversion";

// --- FORMATTERS ---
const formatCLP = (num) => new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
}).format(num);

const formatCrypto = (num) => new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6
}).format(num);

// --- TOTAL INVESTMENT CARD ---
const TotalInvestmentCard = ({ totalCLP, loading }) => (
  <div style={{
    background: 'linear-gradient(135deg, #261f87 0%, #7c4dff 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '12px',
    marginBottom: '30px',
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
    textAlign: 'center'
  }}>
    <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '1px' }}>
      Saldo Total de Inversiones
    </h3>
    {loading ? (
      <div style={{ fontSize: '2rem' }}>Calculando...</div>
    ) : (
      <div style={{ fontSize: '3.5rem', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
        {formatCLP(totalCLP)}
      </div>
    )}
    <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '5px' }}>
      Incluye Billetera, Binance Futures, Staking y BNB
    </div>
  </div>
);

// --- MY ASSETS TABLE ---
const MyAssetsTable = ({ assets }) => (
  <div className="card my-assets-card">
    <div className="assets-header">
      <h3>Mis Inversiones Activas</h3>
    </div>
    <div className="table-responsive">
      <table className="assets-table">
        <thead>
          <tr>
            <th>Activo / Plataforma</th>
            <th>Balance</th>
            <th>Precio Ref.</th>
            <th>Valor Total (CLP)</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset, index) => (
            <tr key={asset.id || index}>
              <td>
                <div className="coin-info">
                  {typeof asset.icon === 'string' ? (
                    <img src={asset.icon} alt={asset.name} className="coin-icon" onError={(e) => e.target.style.display = 'none'} />
                  ) : null}
                  <div>
                    <span className="coin-symbol">{asset.symbol}</span>
                    <span className="coin-name" style={{ fontSize: '0.8rem', color: '#888' }}>{asset.name}</span>
                  </div>
                </div>
              </td>
              <td>
                <span className="amount-crypto">
                  {asset.isFiat ? formatCLP(asset.amount) : formatCrypto(asset.amount)} {asset.unit}
                </span>
              </td>
              <td>
                <span className="amount-fiat">{asset.isFiat ? '-' : formatCLP(asset.priceCLP)}</span>
              </td>
              <td>
                <span style={{ fontWeight: 'bold', color: '#261f87' }}>{formatCLP(asset.valueCLP)}</span>
              </td>
              <td>
                {asset.statusTag ? (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    background: asset.statusColor || '#eee',
                    color: asset.statusTextColor || '#333',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {asset.statusTag}
                  </span>
                ) : (
                  <span style={{ color: '#4caf50', fontSize: '0.8rem' }}>Disponible</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// --- RECENT TRANSACTIONS ---
const RecentTransactions = () => {
  const { user } = useUser();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- HELPER: MAP TYPES ---
  const getReadableType = (tx) => {
    if (tx.description) return tx.description;

    const type = (tx.tipo || '').toLowerCase();
    const coin = (tx.moneda || '').toUpperCase();

    if (type === 'compra' || type === 'buy') return `Compra ${coin}`;
    if (type === 'venta' || type === 'sell') return `Venta ${coin}`;
    if (type === 'claim_staking' || type === 'claim') return 'Reclamo Recompensa Flexible';
    if (type === 'withdraw_staking') return 'Retiro Staking Flexible';
    if (type === 'staking_fixed_30' || type === 'fixed_30') return 'Depósito Plazo Fijo (30d)';
    if (type === 'staking_fixed_90' || type === 'fixed_90') return 'Depósito Plazo Fijo (90d)';
    if (type === 'staking_fixed_180' || type === 'fixed_180') return 'Depósito Plazo Fijo (180d)';
    if (type === 'referral_reward' || type === 'referral') return 'Recompensa Referido';
    if (type === 'deposit') return 'Depósito CLP';
    if (type === 'withdraw') return 'Retiro CLP';

    if (!type && coin) return `Operación ${coin}`;
    return tx.tipo || 'Movimiento';
  };

  const getCurrency = (tx) => {
    if (tx.method === 'CLP') return 'CLP';
    if (tx.method === 'USDT' || tx.method?.includes('Web3')) return 'USDT';
    if (tx.montoCLP > 0) return 'CLP';
    return 'USDT';
  };

  const getAmountColor = (tx) => {
    const type = (tx.tipo || '').toLowerCase();
    const desc = (tx.description || '').toLowerCase();

    if (type.includes('compra') || type.includes('buy') || type.includes('deposit') ||
      type.includes('reward') || type.includes('claim') || desc.includes('compra') || desc.includes('reclamo')) {
      return '#28a745';
    }
    if (type.includes('venta') || type.includes('sell') || type.includes('withdraw') || desc.includes('venta')) {
      return '#dc3545';
    }
    return '#444';
  };

  useEffect(() => {
    if (!user) return;
    const fetchTransactions = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'https://dashboard.xcoin.cl';
        const res = await fetch(`${API_URL}/api/transacciones?userId=${user.uid}&limit=50`);
        const json = await res.json();
        if (json.success && json.data) {
          setTransactions(json.data);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [user]);

  if (loading) return <div className="card"><p>Cargando movimientos...</p></div>;

  return (
    <div className="card recent-transactions-card" style={{ marginTop: '30px' }}>
      <h3>Últimos Movimientos</h3>
      {transactions.length === 0 ? (
        <p style={{ color: '#888', fontStyle: 'italic', padding: '20px' }}>No hay transacciones recientes.</p>
      ) : (
        <div className="table-responsive">
          <table className="assets-table">
            <thead>
              <tr>
                <th>Fecha / Hora</th>
                <th>Tipo</th>
                <th>Moneda</th>
                <th>Cantidad</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id}>
                  <td style={{ fontSize: '0.85rem' }}>
                    {tx.fecha ? new Date(tx.fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td style={{ color: getAmountColor(tx), fontWeight: '500' }}>
                    {getReadableType(tx)}
                  </td>
                  <td style={{ fontWeight: 'bold', color: '#666' }}>
                    {getCurrency(tx)}
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {formatCrypto(tx.cantidad)} {tx.moneda}
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      background: tx.status === 'confirmed' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                      color: tx.status === 'confirmed' ? 'green' : 'orange'
                    }}>
                      {tx.status === 'confirmed' ? 'Confirmado' : tx.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// --- MAIN VIEW ---
const BalanceView = () => {
  const { balances, loadingBalances } = useWallet();
  const { wallet, usdtContract, contract: xcoinContract, refreshTrigger } = useWeb3();
  const { flexibleBalance, lockedDeposits, pendingRewards } = useStaking();

  // Binance State
  const [binanceBalance, setBinanceBalance] = useState(0);
  const [loadingBinance, setLoadingBinance] = useState(true);

  // Web3 State
  const [usdtWeb3, setUsdtWeb3] = useState('0');
  const [xcoWeb3, setXcoWeb3] = useState('0');
  const [bnbWeb3, setBnbWeb3] = useState('0'); // NEW

  // Prices State
  const [prices, setPrices] = useState({ BTC: 0, ETH: 0, USDT: 900, XCO: 9000, BNB: 0 });
  const [loadingPrices, setLoadingPrices] = useState(true);

  // 1. Fetch Web3 Balances (with public RPC fallback)
  useEffect(() => {
    const fetchOnChain = async () => {
      if (wallet) {
        try {
          const addr = wallet.accounts[0].address;
          
          // Public RPC fallback
          const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
          const XCO_ADDRESS = '0x6508AEB49733f5dAAD56621Bc2AC05193F26b53a';
          const USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955';
          const ERC20_ABI = ['function balanceOf(address) view returns (uint256)'];

          // XCO - try wallet contract, fallback to public RPC
          try {
            if (xcoinContract) {
              setXcoWeb3(ethers.formatUnits(await xcoinContract.balanceOf(addr), 18));
            }
          } catch (e) {
            console.warn("XCO via wallet failed, using public RPC");
            const xcoPublic = new ethers.Contract(XCO_ADDRESS, ERC20_ABI, publicProvider);
            setXcoWeb3(ethers.formatUnits(await xcoPublic.balanceOf(addr), 18));
          }

          // USDT - try wallet contract, fallback to public RPC
          try {
            if (usdtContract) {
              setUsdtWeb3(ethers.formatUnits(await usdtContract.balanceOf(addr), 18));
            }
          } catch (e) {
            console.warn("USDT via wallet failed, using public RPC");
            const usdtPublic = new ethers.Contract(USDT_ADDRESS, ERC20_ABI, publicProvider);
            setUsdtWeb3(ethers.formatUnits(await usdtPublic.balanceOf(addr), 18));
          }

          // BNB - try wallet provider, fallback to public RPC
          try {
            const providerSource = wallet.provider || window.ethereum;
            if (providerSource) {
              const provider = new HybridBrowserProvider(providerSource);
              setBnbWeb3(ethers.formatUnits(await provider.getBalance(addr), 18));
            }
          } catch (e) {
            console.warn("BNB via wallet failed, using public RPC");
            setBnbWeb3(ethers.formatUnits(await publicProvider.getBalance(addr), 18));
          }
        } catch (e) { console.error("Web3 Fetch Error", e); }
      }
    };
    fetchOnChain();
  }, [wallet, usdtContract, xcoinContract, refreshTrigger]);

  // 2. Fetch Binance Balance
  useEffect(() => {
    const fetchBinance = async () => {
      try {
        const fn = httpsCallable(functions, 'obtenerSaldoBinance');
        const res = await fn();
        if (res.data.success) {
          setBinanceBalance(parseFloat(res.data.balance));
        } else {
          setBinanceBalance(0);
        }
      } catch (e) {
        console.error("Binance Fetch Error", e);
        setBinanceBalance(0);
      } finally {
        setLoadingBinance(false);
      }
    };
    fetchBinance();
  }, []);

  // 3. Fetch Prices (MATCHING DASHBOARD LOGIC)
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const symbolsToFetch = ['BTC', 'ETH', 'USDT', 'BNB'];
        const response = await fetch(PRECIOS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: { symbols: symbolsToFetch } })
        });

        let preciosApi = [];
        if (response.ok) {
          const result = await response.json();
          preciosApi = result.data || [];
        }

        // Fallback for USDT (Buda)
        let usdtItem = preciosApi.find(p => p.symbol === 'USDT');
        if (!usdtItem) {
          try {
            const res = await fetch('/buda-api/v2/markets/usdt-clp/ticker');
            if (res.ok) {
              const data = await res.json();
              usdtItem = { symbol: 'USDT', priceInClp: parseFloat(data.ticker.last_price[0]) };
              preciosApi.push(usdtItem);
            }
          } catch (e) { }
        }

        const newPrices = { ...prices };
        preciosApi.forEach(p => {
          newPrices[p.symbol] = p.priceInClp;
        });

        if (newPrices.USDT) {
          newPrices.XCO = newPrices.USDT * 10;
        }

        setPrices(newPrices);
      } catch (e) { console.error("Price Fetch Error", e); } finally { setLoadingPrices(false); }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // --- AGGREGATION & RENDER PREP ---

  // Staking Total
  const stakingFlex = parseFloat(flexibleBalance || 0);
  const stakingLocked = lockedDeposits.reduce((acc, d) => acc + parseFloat(d.amount), 0);
  const stakingTotal = stakingFlex + stakingLocked;

  // Wallet
  const walletXCO = wallet ? parseFloat(xcoWeb3) : (balances?.XCO || 0);
  const walletUSDT = wallet ? parseFloat(usdtWeb3) : 0;
  const walletBNB = wallet ? parseFloat(bnbWeb3) : 0; // NEW
  const walletCLP = balances?.CLP || 0;
  const walletBTC = balances?.BTC || 0;
  const walletETH = balances?.ETH || 0;

  // Inventory Building
  const assets = [
    {
      id: 'clp', symbol: 'CLP', name: 'Pesos Chilenos (Billetera)', icon: clpIcon,
      amount: walletCLP, unit: 'CLP',
      priceCLP: 1, valueCLP: walletCLP, isFiat: true
    },
    {
      id: 'binance', symbol: 'USDT', name: 'Trading (Renta Variable)', icon: 'https://cryptologos.cc/logos/tether-usdt-logo.png?v=022',
      amount: binanceBalance, unit: 'USDT',
      priceCLP: prices.USDT, valueCLP: binanceBalance * prices.USDT,
      statusTag: 'Robots', statusColor: 'rgba(76, 175, 80, 0.2)', statusTextColor: '#2e7d32'
    },
    {
      id: 'staking', symbol: 'XCO', name: 'Staking (Renta Fija)', icon: xcnIcon,
      amount: stakingTotal, unit: 'XCO',
      priceCLP: prices.XCO, valueCLP: stakingTotal * prices.XCO,
      statusTag: stakingTotal > 0 ? 'Generando Interés' : 'Retenido',
      statusColor: stakingTotal > 0 ? 'rgba(33, 150, 243, 0.2)' : '#eee',
      statusTextColor: stakingTotal > 0 ? '#1565c0' : '#888'
    },
    {
      id: 'xco', symbol: 'XCO', name: 'XCoin (Disponible)', icon: xcnIcon,
      amount: walletXCO, unit: 'XCO',
      priceCLP: prices.XCO, valueCLP: walletXCO * prices.XCO
    },
    {
      id: 'usdt', symbol: 'USDT', name: 'Tether (Billetera)', icon: 'https://cryptologos.cc/logos/tether-usdt-logo.png?v=022',
      amount: walletUSDT, unit: 'USDT',
      priceCLP: prices.USDT, valueCLP: walletUSDT * prices.USDT
    },
    {
      id: 'bnb', symbol: 'BNB', name: 'BNB (Gas)', icon: 'https://cryptologos.cc/logos/bnb-bnb-logo.png?v=022',
      amount: walletBNB, unit: 'BNB',
      priceCLP: prices.BNB, valueCLP: walletBNB * prices.BNB
    },
    {
      id: 'btc', symbol: 'BTC', name: 'Bitcoin (Custodia)', icon: btcIcon,
      amount: walletBTC, unit: 'BTC',
      priceCLP: prices.BTC, valueCLP: walletBTC * prices.BTC
    },
    {
      id: 'eth', symbol: 'ETH', name: 'Ethereum (Custodia)', icon: ethIcon,
      amount: walletETH, unit: 'ETH',
      priceCLP: prices.ETH, valueCLP: walletETH * prices.ETH
    }
  ];

  const grandTotalCLP = assets.reduce((acc, a) => acc + a.valueCLP, 0);
  const loading = loadingBalances || loadingPrices || loadingBinance;

  return (
    <div className="balance-view" style={{ paddingBottom: '50px' }}>
      <TotalInvestmentCard totalCLP={grandTotalCLP} loading={loading} />
      <MyAssetsTable assets={assets} />
      <RecentTransactions />
    </div>
  );
};

export default BalanceView;
